<template>
	<div class="search-wrapper" ref="wrapperRef" v-focus-section v-tooltip.bottom="$t('search.search_tooltip')">
		<button
			id="search"
			v-focus
			@click.stop="visible ? closeInput() : showInput()"
			@keydown.enter.prevent="visible ? closeInput() : showInput()"
		>
			<IconCloseBtn v-if="visible" />
			<IconSearch v-else />
		</button>
		<Transition name="slide">
			<div v-if="visible" class="input-wrapper" v-focus-section @click.stop>
				<input
					type="text"
					v-model="query"
					@input="handleInput"
					ref="inputRef"
					v-focus
					:placeholder="$t('search.placeholder')"
					class="search-input"
				/>
			</div>
		</Transition>
	</div>
</template>

<script lang="ts" setup>
	import { ref, watch, onBeforeUnmount, nextTick } from "vue"
	import { useRouter, useRoute } from "vue-router"
	import debounce from "lodash.debounce"
	import IconSearch from "../icons/IconSearch.vue"
	import IconCloseBtn from "../icons/IconCloseBtn.vue"

	const router = useRouter()
	const route = useRoute()

	const visible = ref(false)
	const query = ref(route.query.q || "")
	const wrapperRef = ref<HTMLElement | null>(null)
	const inputRef = ref<HTMLInputElement | null>(null)
	const emits = defineEmits<{
		(e: "searchOpened"): void
		(e: "searchClosed"): void
	}>()

	const showInput = async () => {
		visible.value = true
		emits("searchOpened")
		await nextTick()

		// Move spatial-navigation focus to the input
		const el = inputRef.value
		if (el) {
			el.focus() // standard DOM focus for web
			// For TVs (spatial nav):
			;(window as any).$spatialNavigation?.focus(el)
		}
	}

	const closeInput = () => {
		visible.value = false
		emits("searchClosed")
		router.push({
			name: "Home",
		})
	}

	// Handle click outside
	const handleClickOutside = (event: MouseEvent) => {
		if (wrapperRef.value && !wrapperRef.value.contains(event.target as Node)) {
			visible.value = false
			emits("searchClosed")
		}
	}

	// Add/remove event listener dynamically when visible changes
	watch(visible, (val) => {
		if (val) {
			// Delay adding the listener to avoid capturing the current click
			setTimeout(() => {
				document.addEventListener("mousedown", handleClickOutside)
			}, 0)
		} else {
			document.removeEventListener("mousedown", handleClickOutside)
		}
	})

	// Cleanup on unmount
	onBeforeUnmount(() => {
		document.removeEventListener("mousedown", handleClickOutside)
	})

	const handleInput = debounce(() => {
		const path = route.path.toLowerCase()
		let index = "general"

		if (path.startsWith("/movies")) {
			index = "movies"
		} else if (path.startsWith("/series")) {
			index = "series"
		}

		router.push({
			name: "SearchPage",
			query: {
				q: query.value,
				index,
			},
		})
	}, 300)
</script>

<style lang="scss" scoped>
	#search {
		background: transparent;
		border: 0px;
		box-shadow: none;
		svg {
			width: 32px;
			height: 32px;

			@include br.respond-to(br.$desktop-medium) {
				width: 26px;
				height: 26px;
			}
			@include br.tablet-down {
				width: 22px;
				height: 22px;
			}
		}
		padding: 0px;
	}
	.search-wrapper {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		margin-left: auto;
	}

	.input-wrapper {
		overflow: hidden;
		display: inline-block;
		white-space: nowrap;
		border-radius: 12px;
	}

	.slide-enter-active,
	.slide-leave-active {
		transition: width 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease;
	}

	.slide-enter-from {
		width: 0;
		opacity: 0;
	}

	.slide-enter-to {
		width: 200px;
		opacity: 1;
	}

	.slide-leave-from {
		width: 200px;
		opacity: 1;
	}

	.slide-leave-to {
		width: 0;
		opacity: 0;
	}

	.search-input {
		width: 100%;
		box-sizing: border-box;
		background: white;
		border: 1px solid rgba(255, 255, 255, 0.5);
		border-radius: 12px;
		padding: 6px 12px;
		font-size: 16px;
		color: black;
	}
</style>
